import type { TFunction } from 'i18next'

export type WatermarkingLeakStatus =
  | 'processing'
  | 'inconclusive'
  | 'failed'
  | 'completed'

type LeakTrace = {
  status: WatermarkingLeakStatus
  detected_watermark_data: Array<unknown>
}

type LeakStatus = {
  /** The label shown in the leak tracing table's status column. */
  label: string
  /** What went wrong, and what the user can do about it. */
  tooltip: string
}

/**
 * Describes a leak trace's outcome. An unrecognised status is reported as still
 * processing, since the backend only sends terminal states it knows we render.
 */
export const getWatermarkingLeakStatus = (
  { status, detected_watermark_data }: LeakTrace,
  t: TFunction,
): LeakStatus => {
  switch (status) {
    case 'failed':
      return {
        label: t('watermarking:statusLeakFailed', 'Failed'),
        tooltip: t(
          'watermarking:tooltipLeakFailed',
          "The check couldn't be completed. Try re-uploading the file, or contact support if the problem continues.",
        ),
      }
    case 'inconclusive':
      return {
        label: t('watermarking:statusLeakInconclusive', 'Inconclusive'),
        tooltip: t(
          'watermarking:tooltipLeakInconclusive',
          "Watermark data was detected, but it could not be decoded. This often happens with short or low-quality clips. Try a longer or higher-quality copy, if available. If you're confident this track was watermarked and it still doesn't resolve, contact support.",
        ),
      }
    case 'completed':
      if (detected_watermark_data.length === 0) {
        return {
          label: t(
            'watermarking:statusLeakCompletedNotDetected',
            'Not detected',
          ),
          tooltip: t(
            'watermarking:tooltipLeakCompletedNotDetected',
            'This track was not detected as watermarked from this DISCO.',
          ),
        }
      }
      return {
        label: t('watermarking:statusLeakCompleted', 'Completed'),
        tooltip: t(
          'watermarking:tooltipLeakCompleted',
          'Your leak trace is complete. View the results in the table at left.',
        ),
      }
    default:
      return {
        label: t('watermarking:statusLeakProcessing', 'Processing'),
        tooltip: t(
          'watermarking:tooltipLeakProcessing',
          "Your leak trace is underway. You'll be notified in the app when it's complete.",
        ),
      }
  }
}
